import { get } from "lodash";
import React from "react";
import { Control, FieldPath, FieldValues, UseFormRegister, useFormState } from "react-hook-form";
import { MessageDescriptor } from "react-intl";
import { FormFeedback, FormGroup, Input, InputProps, Label } from "reactstrap";
import { Message } from "shared/components/Message";
import { TranslatedRegisterOptions, useTranslatedValidations } from "shared/validations/useTranslatedValidations";

interface IProps<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TContext extends object = object> {
    control: Control<TFieldValues, TContext>;
    name: TFieldName;
    register: UseFormRegister<TFieldValues>;
    options?: TranslatedRegisterOptions<TFieldValues, TFieldName>;
    message: MessageDescriptor;
    id: string;
}

type Props<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TContext extends object = object>
    = IProps<TFieldValues, TFieldName, TContext> & Omit<InputProps, "name" | "onChange" | "onBlur" | "type" | "id">;

export function LabelledCheckbox<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TContext extends object = object>({
    control,
    name,
    register,
    options,
    message,
    id,
    ...props
}: Props<TFieldValues, TFieldName, TContext>) {
    const registerOptions = useTranslatedValidations(options);
    const { errors } = useFormState({ control });
    const { ref, ...rest } = register(name, registerOptions);

    const errorMessage = get(errors, `${name}.message`) as string | undefined;

    return (
        <FormGroup check={true} className="mb-3">
            <Label check={true} htmlFor={id}>
                <Input
                    type="checkbox"
                    id={id}
                    innerRef={ref}
                    invalid={!!errorMessage}
                    aria-invalid={!!errorMessage}
                    aria-describedby={errorMessage ? `${id}-error` : undefined}
                    {...props}
                    {...rest}
                />
                <Message message={message} />
            </Label>
            {errorMessage && (
                <FormFeedback id={`${id}-error`} className="d-block">
                    {errorMessage}
                </FormFeedback>
            )}
        </FormGroup>
    );
}
